/* eslint-disable no-param-reassign */
import {
  largeCurrencyFormat,
  largeDecimalFormat,
  smallCurrencyFormat,
  xStandardSuffixFormat,
} from 'common/formats/formats';
import { Cells } from 'common/types/scalarSpreadsheet';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { WeightedShareValuesCellCustomData } from 'pages/ValuationsAllocation/allocation/WeightedShareValues/config';
import { ALLOCATION_STATIC_CUSTOM_FORMAT } from 'pages/ValuationsAllocation/common/constants/allocation';
import {
  ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_FUND_SEPARATOR_KEY,
  ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_SECURITY_TOTAL_KEY,
  SHEET_ALIASES_CONSTANTS,
  SHEET_TITLES_CONSTANTS,
} from 'pages/ValuationsAllocation/common/constants/weightedShareValues';
import { getExpression, getNumberValue, getObjectValue, getStringValue } from 'utilities';
import { alphabetGenerator, getColumnLetter } from 'utilities/alphabet-utilities';
import { CellParserParams, WeightedShareValuesParserParams } from './types';
import { generateWeightedValuePerShareExpression } from './utils';

const { ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_HEADER_TITLE } = SHEET_ALIASES_CONSTANTS;

const { ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_HEADERS, getAllocationWeightedShareValuesHeader }
  = SHEET_TITLES_CONSTANTS;

const { INVESTED_CAPITAL, WEIGHTED_VALUE_PER_SHARE, MOIC, NUMBER_OF_SHARES, VALUE }
  = ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_HEADERS;

let fundInvestedCapital = 0;

const handleSecurityTotalRow = (
  cell: ICell,
  colNumber: number,
  columnLegend: string,
  fundSeparatorIntervals: { start: number; end: number },
  partialFundInvestedCapital: number
) => {
  const startCell = `${columnLegend}${fundSeparatorIntervals.start}`;
  const inBetweenCells = Array.from({ length: fundSeparatorIntervals.end - fundSeparatorIntervals.start - 1 }).map((_, i) => `${columnLegend}${i + fundSeparatorIntervals.start + 1}`);
  const endCell = `${columnLegend}${fundSeparatorIntervals.end}`;

  const cellRange = [startCell, ...inBetweenCells, endCell].join(',');

  const defaultColumnSumEquation = startCell === endCell ? `=SUM(${startCell})` : `=SUM(${cellRange})`;
  
  // For MOIC column, we need to use VALUE column cells instead of MOIC column cells
  const valueColumnLegend = getColumnLetter(VALUE.columnNumber - 1);
  const valueStartCell = `${valueColumnLegend}${fundSeparatorIntervals.start}`;
  const valueInBetweenCells = Array.from({ length: fundSeparatorIntervals.end - fundSeparatorIntervals.start - 1 }).map((_, i) => `${valueColumnLegend}${i + fundSeparatorIntervals.start + 1}`);
  const valueEndCell = `${valueColumnLegend}${fundSeparatorIntervals.end}`;
  const valueCellRange = [valueStartCell, ...valueInBetweenCells, valueEndCell].join(',');
  const valueSumExpression = valueStartCell === valueEndCell ? `SUM(${valueStartCell})` : `SUM(${valueCellRange})`;
  
  const defaultColumnMOICEquation = `=IF(${partialFundInvestedCapital}>0,${valueSumExpression}/${partialFundInvestedCapital},0)`;
  
  const totalConfiguration: Map<number, Pick<ICell, 'expr' | 'value'> & { propertyType: string }> = new Map([
    [INVESTED_CAPITAL.columnNumber, {
      expr: defaultColumnSumEquation,
      value: defaultColumnSumEquation,
      propertyType: 'INVESTED_CAPITAL'
    }],
    [NUMBER_OF_SHARES.columnNumber, {
      expr: defaultColumnSumEquation,
      value: defaultColumnSumEquation,
      propertyType: 'NUMBER_OF_SHARES'
    }],
    [VALUE.columnNumber, {
      expr: defaultColumnSumEquation,
      value: defaultColumnSumEquation,
      propertyType: 'VALUE'
    }],
    [MOIC.columnNumber, {
      expr: defaultColumnMOICEquation,
      value: defaultColumnMOICEquation,
      propertyType: 'MOIC'
    }]
  ]);

  if (totalConfiguration.has(colNumber)) {
    const { expr, value, propertyType } = totalConfiguration.get(colNumber)!;
    cell.value = value;
    cell.expr = expr;
    cell.gridType = 'number';
    const customKey = `${cell.customKey}_${propertyType}`;
    cell.customKey = customKey;
  } else {
    cell.readOnly = true;
    cell.hidden = true;
    cell.value = '';
    cell.expr = '';
  }
};

const cellParser = (params: CellParserParams) => {
  const { allocationScenarios, allocationSecurityNames, alphabet, colIndex, column, isUniformCurrency, row, rowIndex, fundSeparatorIntervals, relatedEntityId } = params;

  const colNumber = colIndex + 1;
  const rowNumber = rowIndex + 1;
  const columnLegend = alphabet[colIndex];
  const cellKey = columnLegend + rowNumber;

  const { investedCapital, isCustomSecurity, security, value: columnValue } = getObjectValue(column[row.alias]);

  const getSumProductExpr = () => {
    const { presentValuePerShareCells, scenarioWeightingProbabilityCells } = generateWeightedValuePerShareExpression({
      allocationScenarios,
      isUniformCurrency,
      security: row.value?.toString(),
      relatedEntityId,
    });

    let weightedValuePerShareExpression = '';
    if (allocationScenarios.length > 1) {
      weightedValuePerShareExpression = `=SUMPRODUCT([${scenarioWeightingProbabilityCells}], [${presentValuePerShareCells}])`;
    } else if (allocationScenarios.length === 1) {
      // If there is only one allocation scenario, we assume its probability is 100%
      weightedValuePerShareExpression = `=${presentValuePerShareCells}`;
    }
    return weightedValuePerShareExpression;
  };

  // Creating Cell from Row and Column
  const cell = { ...row, key: cellKey, value: columnValue };

  const investedCapitalColumn = getColumnLetter(INVESTED_CAPITAL.columnNumber - 1);
  const investedCapitalCell = `${investedCapitalColumn}${rowNumber}`;

  const weightedValuePerShareColumn = getColumnLetter(WEIGHTED_VALUE_PER_SHARE.columnNumber - 1);
  const weightedValuePerShareCell = `${weightedValuePerShareColumn}${rowNumber}`;

  const numberOfSharesColumn = getColumnLetter(NUMBER_OF_SHARES.columnNumber - 1);
  const numberOfSharesCell = `${numberOfSharesColumn}${rowNumber}`;

  const valueColumn = getColumnLetter(VALUE.columnNumber - 1);
  const valueCell = `${valueColumn}${rowNumber}`;

  // Parse Cell based on Row alias
  if (row.alias === ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_HEADER_TITLE) {
    cell.value = getStringValue(getAllocationWeightedShareValuesHeader(colNumber)?.title);
  } else {
  // Rest of the Rows (Securities)
    // Parse Cell based on Column number
    switch (colNumber) {
      case INVESTED_CAPITAL.columnNumber:
        cell.expr = `=${investedCapital}`;
        cell.format = largeCurrencyFormat;
        cell.gridType = 'number';
        break;

      case WEIGHTED_VALUE_PER_SHARE.columnNumber:
        cell.customFormat = ALLOCATION_STATIC_CUSTOM_FORMAT;
        // Securities without an Equity Allocation row (convertible notes, unissued options,
        // custom-security ownerships) have no `<name>_<col>` cell to point at, so referencing
        // one would leave a dangling symbol that invalidates the whole sheet.
        cell.expr = allocationSecurityNames.has(getStringValue(row.value?.toString())) ? getSumProductExpr() : null;
        cell.format = smallCurrencyFormat;
        cell.gridType = 'number';
        if (!cell.expr) {
          cell.readOnly = true;
        }
        break;

      case NUMBER_OF_SHARES.columnNumber:
        cell.format = largeDecimalFormat;
        cell.gridType = 'number';
        break;

      case VALUE.columnNumber:
        cell.allowNegativeValue = false;
        cell.expr = row.isExpr ? `=${weightedValuePerShareCell}*${numberOfSharesCell}` : null;
        cell.format = largeCurrencyFormat;
        cell.gridType = 'number';
        cell.minValue = 0;
        // any is justified due to the generic typing of T on the cell that is also on the row data property
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (cell as ICell & { isRelatedEntityInvestmentInCompany: boolean }).isRelatedEntityInvestmentInCompany = (row as any).data?.isRelatedEntityInvestmentInCompany;

        if (isCustomSecurity) {
          cell.expr = null;
          cell.readOnly = false;
        }
        break;

      case MOIC.columnNumber:
        cell.expr = `=IF(${investedCapitalCell}>0,${valueCell}/${investedCapitalCell},0)`;
        cell.format = xStandardSuffixFormat;
        cell.gridType = 'number';
        fundInvestedCapital += getNumberValue(investedCapital);
        break;

      default:
        break;
    }
  }

  if (row.alias === ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_SECURITY_TOTAL_KEY) {
    handleSecurityTotalRow(cell, colNumber, columnLegend, fundSeparatorIntervals, fundInvestedCapital);
    if (colNumber === MOIC.columnNumber) {
      fundInvestedCapital = 0;
    }
  }

  if (row.alias === ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_FUND_SEPARATOR_KEY) {
    cell.value = '';
    cell.expr = '';
  }


  // Custom Data for Weighted Share Values
  cell.data = {
    ...getObjectValue(cell.data),
    isCustomSecurity,
    security,
  } as WeightedShareValuesCellCustomData;

  // Set default Cell values
  const { expr = '', format = null, gridType = 'string', value = '' } = getObjectValue(cell);

  return {
    [cell.key]: {
      ...cell,
      columnLegend,
      expr: getExpression({ expr, columnLegend }),
      format,
      gridType,
      value,
    } as ICell,
  };
};

const customParser = (params: WeightedShareValuesParserParams) => {
  const { allocationScenarios, allocationSecurityNames, columns, isUniformCurrency, rowConfig, relatedEntityId } = params;

  let cells = {} as Cells;
  const alphabet = alphabetGenerator([], columns.length);

  const fundSeparatorIntervals = {
    start: 0,
    end: 0,
  };

  rowConfig.forEach((row, rowIndex: number) => {
    if (row.alias === ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_FUND_SEPARATOR_KEY) {
      // +2 because looping is based from 0 (+1) and we want to start counting from the first row after the fund-separator (+1)
      fundSeparatorIntervals.start = rowIndex + 2;
    }

    if (row.alias === ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_SECURITY_TOTAL_KEY) {
      fundSeparatorIntervals.end = rowIndex;
    }

    columns.forEach((column, colIndex: number) => {
      cells = {
        ...cells,
        ...cellParser({
          allocationScenarios,
          allocationSecurityNames,
          alphabet,
          colIndex,
          column,
          isUniformCurrency,
          row,
          rowIndex,
          fundSeparatorIntervals,
          relatedEntityId,
        }),
      };
    });
  });

  return cells;
};

export default customParser;
